import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FilePlus2, ClipboardCheck, Library, HelpCircle, ListTree, FolderTree, PenTool,
  Wand2, Quote, Bot, Microscope, Gauge, Download, Code2, ShieldCheck,
  Printer, ArrowLeft, Lightbulb, ArrowRight, BookOpen, GraduationCap,
  FlaskConical, CalendarClock,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { GUIDE_UI, GUIDE_QUICKSTART, GUIDE_SECTIONS } from '@/content/guide';
import InkPhDLogo from '@/components/InkPhDLogo';
import AppFooter from '@/components/AppFooter';
import SeoHead from '@/components/SeoHead';

const ICONS = {
  FilePlus2, ClipboardCheck, Library, HelpCircle, ListTree, FolderTree, PenTool,
  Wand2, Quote, Bot, Microscope, Gauge, Download, Code2, ShieldCheck,
  GraduationCap, FlaskConical, CalendarClock, BookOpen,
};

const LANG_KEY = 'inkphd-guide-lang';

export default function GuidePage({
  ui = GUIDE_UI,
  quickstart = GUIDE_QUICKSTART,
  sections = GUIDE_SECTIONS,
  path = '/guide',
  sibling = { to: '/guide/dissertation', label: { en: 'Dissertation guide', bg: 'Ръководство за дисертация' } },
}) {
  const [lang, setLang] = useState('en');
  const [active, setActive] = useState(sections[0].id);

  useEffect(() => {
    const saved = window.localStorage.getItem(LANG_KEY);
    if (saved === 'bg' || saved === 'en') setLang(saved);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      const current = sections.filter((s) => {
        const el = document.getElementById(s.id);
        return el && el.getBoundingClientRect().top < 160;
      }).pop();
      if (current) setActive(current.id);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [sections]);

  const t = ui[lang];
  const pick = (v) => (v && typeof v === 'object' ? v[lang] : v);

  const setLanguage = (next) => {
    setLang(next);
    window.localStorage.setItem(LANG_KEY, next);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-[#0a0e1a] text-foreground thesis-brand">
      <SeoHead
        title={`InkPhD · ${t.title}`}
        path={path}
        description={pick({ en: ui.en.subtitle, bg: ui.bg.subtitle })} />

      <header className="border-b border-border bg-card/80 backdrop-blur sticky top-0 z-20 print:hidden">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/thesis" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
            data-testid="guide-back-link">
            <ArrowLeft className="w-4 h-4" /><span className="hidden sm:inline">{t.backToApp}</span>
          </Link>
          <Link to="/guides" className="text-[11px] uppercase tracking-widest text-muted-foreground
            hover:text-foreground hidden sm:inline" data-testid="guide-index-link">
            {lang === 'bg' ? 'Всички ръководства' : 'All guides'}
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <InkPhDLogo size="sm" />
            <div className="flex rounded-full border border-border overflow-hidden" data-testid="guide-lang-toggle">
              {['en', 'bg'].map((code) => (
                <button key={code} onClick={() => setLanguage(code)}
                  data-testid={`guide-lang-${code}`}
                  className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors
                    ${lang === code ? 'bg-[#0b1a35] text-white' : 'text-muted-foreground hover:bg-muted'}`}>
                  {code}
                </button>
              ))}
            </div>
            <Button asChild variant="ghost" size="sm" className="rounded-full hidden md:inline-flex"
              data-testid="guide-sibling-link">
              <Link to={sibling.to}>{sibling.label[lang]} →</Link>
            </Button>
            <Button variant="outline" size="sm" className="rounded-full hidden sm:inline-flex"
              onClick={() => window.print()} data-testid="guide-print-btn">
              <Printer className="w-3.5 h-3.5 mr-1.5" />{t.print}
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-6xl mx-auto px-3 sm:px-6 py-8 sm:py-14" data-testid="guide-page">
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-[#1d4ed8]/35
            bg-[#1d4ed8]/[0.06] px-3 py-1 text-[11px] font-semibold uppercase tracking-widest
            text-[#1d4ed8] dark:text-[#7aa9ff] mb-5">
            <BookOpen className="w-3 h-3" /> {t.badge}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-semibold tracking-tight mb-5"
            style={{ fontFamily: '"Fraunces", Georgia, serif' }} data-testid="guide-title">
            {t.title}
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed">{t.subtitle}</p>
        </div>

        {/* Quick start */}
        <section className="mb-16" data-testid="guide-quickstart">
          <h2 className="text-base md:text-lg font-semibold mb-1">{t.quickstart}</h2>
          <p className="text-sm text-muted-foreground mb-5 max-w-2xl">{t.quickstartLead}</p>
          <ol className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {quickstart[lang].map((q, i) => (
              <li key={q.to} className="rounded-xl border border-border p-4 hover:border-[#1d4ed8]/40
                transition-colors bg-card/40">
                <a href={q.to} className="block group">
                  <span className="text-[11px] font-mono text-muted-foreground">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-sm font-semibold mt-1 group-hover:text-[#1d4ed8] transition-colors">
                    {q.t} <ArrowRight className="w-3 h-3 inline-block ml-0.5" />
                  </p>
                  <p className="text-[12px] text-muted-foreground mt-1 leading-snug">{q.d}</p>
                </a>
              </li>
            ))}
          </ol>
        </section>

        <div className="lg:grid lg:grid-cols-[230px_1fr] lg:gap-12">
          {/* Sidebar */}
          <nav className="hidden lg:block print:hidden" data-testid="guide-toc">
            <div className="sticky top-24">
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">
                {t.contents}
              </p>
              <ul className="space-y-0.5 border-l border-border">
                {sections.map((s, i) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} data-testid={`guide-toc-${s.id}`}
                      className={`block pl-3 -ml-px py-1.5 text-[12.5px] leading-snug border-l-2 transition-colors
                        ${active === s.id
                          ? 'border-[#1d4ed8] text-foreground font-semibold'
                          : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                      <span className="font-mono text-[10px] mr-1.5 opacity-60">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      {s.title[lang]}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          {/* Sections */}
          <div className="space-y-16 min-w-0">
            {sections.map((s, i) => {
              const Icon = ICONS[s.icon] || BookOpen;
              return (
                <section key={s.id} id={s.id} className="scroll-mt-24" data-testid={`guide-section-${s.id}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-[#1d4ed8]/10 dark:bg-[#1d4ed8]/20 flex items-center
                      justify-center shrink-0">
                      <Icon className="w-4 h-4 text-[#1d4ed8] dark:text-[#7aa9ff]" />
                    </div>
                    <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                      {t.step} {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-serif font-semibold tracking-tight mb-3"
                    style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
                    {s.title[lang]}
                  </h2>
                  <p className="text-[15px] text-foreground/85 leading-relaxed mb-5">{s.lead[lang]}</p>
                  <ol className="space-y-2.5 mb-5">
                    {s.steps[lang].map((step, j) => (
                      <li key={j} className="flex gap-3 text-[14px] leading-relaxed">
                        <span className="font-mono text-[11px] text-[#1d4ed8] dark:text-[#7aa9ff] mt-1 shrink-0">
                          {String(j + 1).padStart(2, '0')}
                        </span>
                        <span className="text-foreground/85">{step}</span>
                      </li>
                    ))}
                  </ol>
                  <div className="rounded-lg border border-amber-500/35 bg-amber-500/[0.06] p-3.5 flex gap-2.5">
                    <Lightbulb className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-[13px] leading-relaxed">
                      <strong className="uppercase text-[10px] tracking-widest mr-1.5">{t.tip}</strong>
                      {s.tip[lang]}
                    </p>
                  </div>
                </section>
              );
            })}

            <div className="rounded-xl border border-border bg-muted/30 p-5" data-testid="guide-footnote">
              <p className="text-[13px] text-muted-foreground leading-relaxed">{t.footNote}</p>
              <div className="flex flex-wrap gap-3 mt-4">
                <Button asChild className="rounded-full bg-[#0b1a35] hover:bg-[#1d4ed8] text-white"
                  data-testid="guide-start-btn">
                  <Link to="/thesis">{t.backToApp} <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
                </Button>
                <Button variant="outline" className="rounded-full sm:hidden"
                  onClick={() => window.print()} data-testid="guide-print-btn-mobile">
                  <Printer className="w-3.5 h-3.5 mr-1.5" />{t.print}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <AppFooter lang="en" />
    </div>
  );
}
